import React, { useState, useMemo, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { BREAKPOINTS, VIDEO_EXTENSIONS } from '../../../constants';
import { useMatchesMinWidth } from '../../hooks/useMatchesMinWidth';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import ImageShowcaseSwiper from './ImageShowcaseSwiper';
import BetaIcon, { IconColors, IconSizes } from '../Icons/Icon';
import Button, { ButtonSizes, ButtonStyles } from '../Button/Button';
import Typography, {
  TypographyStates,
  TypographyVariants,
} from '../../ui-components/Typography/Typography';
import {
  blockScrollDesktop,
  blockScrollMobile,
  restoreScrollDesktop,
  restoreScrollMobile,
} from '../../../widget/utils/blockScroll';

const LIGHTBOX_BACKGROUND_COLOR = '#181f36cc';
const DEFAULT_SIZE = 640;
const SIZES = [1600, 1366, 1024, 768, 640];

const Slideshow = ({
  images,
  title,
  initialIndex = 0,
  onClose,
  isOpen,
  setIsOpen,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const actualIsOpen = isOpen !== undefined ? isOpen : internalIsOpen;
  const toggleOpen = setIsOpen || setInternalIsOpen;

  const isDeviceSmallerThanMblLandscape = !useMatchesMinWidth(
    BREAKPOINTS.MBL_LANDSCAPE
  );
  const viewportWidth = window.innerWidth;

  const screenSize =
    SIZES.find((size) => viewportWidth >= size) || DEFAULT_SIZE;
  const dprProp = isDeviceSmallerThanMblLandscape ? '&dpr=2' : '';
  const parsedImages = JSON.parse(images);

  const videoExtensions = VIDEO_EXTENSIONS;

  // check video logic
  const isVideo = (src) =>
    typeof src === 'string' &&
    videoExtensions.some((ext) => src.toLowerCase().endsWith(ext));

  const parseMedia = (media, size) => {
    if (typeof media === 'string') {
      return {
        src: isVideo(media) ? media : `${media}?width=${size}${dprProp}`,
        original_src: media,
        type: isVideo(media) ? 'video' : 'image',
        alt: `${title}`,
      };
    }

    return {
      src: media.is_video ? media.url : `${media.url}?width=${size}${dprProp}`,
      original_src: media.url,
      type: media.is_video ? 'video' : 'image',
      alt: `${title}`,
    };
  };

  // separate community and official media
  const officialMedia = parsedImages.filter(
    (media) => typeof media === 'string' || media.provider === 'partner'
  );
  const communityMedia = parsedImages.filter(
    (media) => typeof media !== 'string' && media.provider === 'customer'
  );

  const mediaOfficial = officialMedia.map((media) =>
    parseMedia(media, screenSize)
  );
  const mediaCommunity = communityMedia.map((media) => ({
    type: media.is_video ? 'video' : 'image',
    src: media.is_video
      ? media.url
      : `${media.url}?width=${screenSize}${dprProp}`,
    original_src: media.url,
    alt: media.alt || `${title}`,
    date: media.order_booking_datetime || media.created_at || null,
    first_name: media.customer_first_name || null,
    last_name: media.customer_last_name || null,
    profile_image: media.customer_media || null,
    rating: media.rating || null,
  }));

  const mediaCommunityFiltered = mediaCommunity.filter(
    (item) => item.rating > 2
  );

  // useEffect(() => {
  //   if (initialIndex !== null && initialIndex !== undefined) {
  //     toggleOpen(true);
  //   }
  // }, [initialIndex]);

  useEffect(() => {
    if (actualIsOpen) {
      if (isDeviceSmallerThanMblLandscape) {
        blockScrollMobile();
      } else {
        blockScrollDesktop();
      }
    } else {
      if (isDeviceSmallerThanMblLandscape) {
        restoreScrollMobile();
      } else {
        restoreScrollDesktop();
      }
    }

    return () => {
      if (isDeviceSmallerThanMblLandscape) {
        restoreScrollMobile();
      } else {
        restoreScrollDesktop();
      }
    };
  }, [actualIsOpen, onClose]);

  const totMedia = mediaOfficial.length + mediaCommunityFiltered.length;
  return (
    <>
      <Button
        size={ButtonSizes.xs}
        style={ButtonStyles.tonal}
        iconLeft={
          <BetaIcon
            name={'picture'}
            color={IconColors.ghost}
            size={IconSizes[16]}
          />
        }
        label={
          <Typography
            variant={TypographyVariants.p3}
            state={TypographyStates.inverted}
          >
            +{totMedia}
          </Typography>
        }
        onClick={() => {
          onClose();
          toggleOpen(true);
        }}
      />

      {actualIsOpen &&
        createPortal(
          <CSSTransition
            classNames="modal-container"
            in={actualIsOpen}
            mountOnEnter={true}
            unmountOnExit={true}
            appear={true}
            timeout={200}
          >
            <div
              key="modal"
              className={'modal-container'}
              style={{
                backgroundColor: LIGHTBOX_BACKGROUND_COLOR,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ImageShowcaseSwiper
                open={actualIsOpen}
                setIsOpen={setIsOpen}
                mediaOfficial={mediaOfficial}
                mediaCommunity={
                  mediaCommunityFiltered.length ? mediaCommunityFiltered : null
                }
                backgroundColor={LIGHTBOX_BACKGROUND_COLOR}
                isDeviceSmallerThanMblLandscape={
                  isDeviceSmallerThanMblLandscape
                }
                officialCounter={mediaOfficial.length}
                communityCounter={mediaCommunityFiltered.length}
                className={'image-showcase-swiper__swiper'}
                initialIndex={initialIndex}
              />
            </div>
          </CSSTransition>,
          document.body
        )}
    </>
  );
};

Slideshow.propTypes = {
  images: PropTypes.any.isRequired,
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
  initialIndex: PropTypes.number,
};

export default Slideshow;
